import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';
import { styled } from 'goober';

import { Flexbox } from '@/ui/flexbox/flexbox';
import { Icon } from '@/ui/icons/icon';
import { Stack } from '@/ui/line/line';

import type { AccountScopedMcpConnectorConfig } from '../mcp-oauth/mcp-oauth-connector-config';
import { McpOAuthSetupTabs } from '../mcp-oauth/mcp-oauth-setup-tabs';
import { SectionDivider, StatusDot } from '../mcp-oauth/mcp-oauth.styles';
import { Card, SetupStepsAnchor } from './claude-mcp-connector.styles';
import { ClaudeMcpConnectorHeader } from './claude-mcp-connector-header';

const INTERCOM_PREFILL_RETRY_MS = 200;

interface ClaudeMcpConnectorWaitingViewProps {
  connectorConfig: AccountScopedMcpConnectorConfig;
  copiedFieldId: string | null;
  onCopy: (value: string, fieldId: string) => void;
  setupStepsRef: RefObject<HTMLDivElement | null>;
  showAdvancedSettingsHint: boolean;
}

export const ClaudeMcpConnectorWaitingView = ({
  connectorConfig,
  copiedFieldId,
  onCopy,
  setupStepsRef,
  showAdvancedSettingsHint,
}: ClaudeMcpConnectorWaitingViewProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const { showNewMessage } = useIntercom();
  const prefillRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(
    () => () => {
      if (prefillRetryTimeoutRef.current) {
        clearTimeout(prefillRetryTimeoutRef.current);
      }
    },
    [],
  );

  const handleHavingTroubleClick = (): void => {
    if (!window.runTimeEnvs.VITE_INTERCOM_APP_ID) {
      return;
    }

    const message = t`mcpOAuth.havingTroubleIntercomMessage`;

    if (prefillRetryTimeoutRef.current) {
      clearTimeout(prefillRetryTimeoutRef.current);
    }

    showNewMessage(message);
    prefillRetryTimeoutRef.current = setTimeout(() => {
      prefillRetryTimeoutRef.current = null;
      showNewMessage(message);
    }, INTERCOM_PREFILL_RETRY_MS);
  };

  return (
    <Card>
      <Stack fullWidth gap="14px">
        <ClaudeMcpConnectorHeader
          subtitle={t`mcpOAuth.cardSubtitle`}
          title={t`mcpOAuth.cardTitle`}
        />

        <WaitingBanner fullWidth gap="0">
          <WaitingBannerTitle alignItems="center" fullWidth gap="8px">
            <StatusDot $status="waiting" />
            <span>{t`mcpOAuth.statusWaitingAuthorized`}</span>
          </WaitingBannerTitle>
          <WaitingBannerDescription>
            {t`mcpOAuth.waitingAuthorizedDescription`}
          </WaitingBannerDescription>
        </WaitingBanner>

        <SetupStepsAnchor defaultRef={setupStepsRef} fullWidth>
          <McpOAuthSetupTabs
            connectorConfig={connectorConfig}
            copiedFieldId={copiedFieldId}
            onCopy={onCopy}
            showAdvancedSettingsHint={showAdvancedSettingsHint}
          />
        </SetupStepsAnchor>

        <SectionDivider />
        <HelpLinkButton onClick={handleHavingTroubleClick} type="button">
          <Icon color="currentColor" height="15px" icon="Info" width="15px" />
          {t`mcpOAuth.havingTroublePrompt`}
        </HelpLinkButton>
      </Stack>
    </Card>
  );
};

const WaitingBanner = styled(Stack)`
  background-color: ${({ theme }) => theme.colors.spotlight.surface.accent};
  border-radius: 8px;
  padding: 12px 14px;
`;

const WaitingBannerTitle = styled(Flexbox)`
  color: ${({ theme }) => theme.colors.blue.primaryB};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 5px;
`;

const WaitingBannerDescription = styled('p')`
  color: ${({ theme }) => theme.colors.gray.c12};
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 12.5px;
  line-height: 1.5;
  margin: 0;
`;

const HelpLinkButton = styled('button')`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.blue.primaryA};
  cursor: pointer;
  display: inline-flex;
  font-family: ${({ theme }) => theme.fonts.neue};
  font-size: 13px;
  gap: 5px;
  padding: 0;
`;
